import { Box, Heading, SimpleGrid, Table, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import _ from "lodash";
import dayjs from "dayjs";
import { IoTimeOutline, IoPeopleOutline, IoAnalyticsOutline } from 'react-icons/io5';


import { checkServiceHasChildren, formatNumber, generateHoursArray } from "../../../../store/helpers";
import LineGraph from "../../../daily/LineGraph";
import StatsCard from "../../../StatsCard";
import EnterpriseReportSummary from "../../../reports/EnterpriseReportSummary";
import { buildRealtimeArrivalSummary } from "../../../reports/RealtimeEnterpriseSummary";

const getServiceData = ({ ticketsClone, branches, servicesSubscriptions, services }) => {
  const branchesFiltered = _.cloneDeep(branches).filter(
    (a) => a.reportStatus != null && Number(a.reportStatus)
  );

  const allServiceIds = new Set();
  branchesFiltered.forEach((branch) => {
    const branchServices =
      _.cloneDeep(servicesSubscriptions).find((s) => s.branchId === branch.id)
        ?.services || [];
    branchServices.forEach((id) => allServiceIds.add(id));
  });

  const leafServices = checkServiceHasChildren(_.cloneDeep(services)).filter(
    (s) => allServiceIds.has(s.id) && !s.hasChildren
  );

  return leafServices
    .map((service) => {
      const count = ticketsClone.filter((t) => t.serviceId === service.id).length;
      return { id: service.id, name: service.name, count };
    })
    .sort((a, b) => b.count - a.count);
};

const ArrivalRatesPerHour = ({ reportIndex }) => {
  const { workDay, organizationId, branches, servicesSubscriptions, services } = useSelector(
    (state) => state.defaultReducer
  );
  const { tickets } = useSelector((state) => state.realtimeReducer);

  let ticketsClone = _.cloneDeep(tickets);

  ticketsClone = ticketsClone.filter(
    (a) => a.organizationId === organizationId
  );
  
  const startTimeStamp = dayjs()
    .set("hour", workDay[0].split(" ")[0])
    .set("minute", 0)
    .unix();
  const endTimeStamp = dayjs().unix();
  const hoursBetween = generateHoursArray(startTimeStamp, endTimeStamp);

  const chart = hoursBetween
    .map((hour) => {
      const hourTickets = ticketsClone.filter(
        (a) => dayjs.unix(a.created).format("h A") === hour
      );

      if (workDay.includes(hour))
        return {
          hour,
          total: hourTickets.length,
        };
      return null;
    })
    .filter(Boolean);

  const min = Math.min(...chart.map((a) => a.total)) || 0;
  const max = Math.max(...chart.map((a) => a.total)) || 0;
  const total = chart.reduce((a, b) => a + b.total, 0) || 0;
  const average = total / chart.length || 0;
  const peakHour = chart.find((a) => a.total === max)?.hour || "NA";
  const currentHourTotal =
    chart.find((a) => a.hour === dayjs().format("h A"))?.total || 0;
  const serviceData = getServiceData({
    ticketsClone,
    branches,
    servicesSubscriptions,
    services,
  });
  const summary = buildRealtimeArrivalSummary({
    total,
    average,
    min,
    max,
    peakHour,
    currentHourTotal,
    serviceData,
    formatNumber,
  });

  const stats = [
    {
      title: 'Total Visitors Today',
      stat: total,
      icon: IoPeopleOutline,
      iconColor: "var(--green)"
    },
    {
      title: 'Peak Hour',
      stat: [peakHour],
      icon: IoAnalyticsOutline,
      iconColor: "var(--red)"
    },
    {
      title: 'Total This Hour',
      stat: currentHourTotal,
      icon: IoTimeOutline,
      iconColor: "var(--green)"
    },
    {
      title: 'Maximum Visitors Per Hour',
      stat: Number.isFinite(max) && !Number.isNaN(max) ? formatNumber(max) : "NA",
      icon: IoPeopleOutline,
      iconColor: "var(--red)"
    },
    {
      title: 'Minimum Visitors Per Hour',
      stat: Number.isFinite(min) && !Number.isNaN(min) ? formatNumber(min) : "NA",
      icon: IoPeopleOutline,
      iconColor: "var(--green)"
    },
    {
      title: 'Average Visitors Per Hour',
      stat: Number.isFinite(average) && !Number.isNaN(average) ? formatNumber(average) : "NA",
      icon: IoPeopleOutline,
      iconColor: "var(--orange)"
    },
  ]

  return (
    <Box className="reportContainer" mt="20px">
      <EnterpriseReportSummary
        title="Realtime Arrival Demand Summary"
        kpis={summary.kpis}
        tables={summary.tables}
        notes={summary.notes}
      />

      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        gap={0}
        mb={10}
      >
        {
          stats.map((a,i) =>
            <Box
              px={"20px"}
              py={"15px"}
              mb={"5px"}
              border={"1px solid var(--borderGrey)"}
              borderRight={{
                base: "1px solid #E5E4E5",
                md: [2,5].includes(i) ? "1px solid #E5E4E5" : "none"
              }}
              borderRadius={{
                base: "10px",
                md: [0,3].includes(i)
                      ? "10px 0 0 10px"
                      : [2,5].includes(i)
                        ? "0 10px 10px 0"
                        : "0"
              }}
            
      
            >
              <StatsCard title={a.title} stat={a.stat} icon={a.icon} iconColor={a.iconColor} />
            </Box>
          )
        }       
      </SimpleGrid>

      <Box mt="20px" mb="20px">
        <LineGraph
          chart={chart}
          height={"300px"}
          title={"Entire Organization Arrival Rates, Hour by Hour Comparison"}
          lineName="Tickets"
        />
      </Box>

      <ServiceBreakdown
        serviceData={serviceData}
      />
    </Box>
  );
};

const ServiceBreakdown = ({ serviceData }) => {
  const totalTickets = serviceData.reduce((acc, s) => acc + s.count, 0);

  if (serviceData.length === 0) return null;

  return (
    <Box mt="30px">
      <Heading fontSize="18px" color="var(--black-dry)" mb="10px">
        Arrivals Per Service
      </Heading>
      <Table.Root className="stripedTable" size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader w="40px">#</Table.ColumnHeader>
            <Table.ColumnHeader>Service</Table.ColumnHeader>
            <Table.ColumnHeader borderLeft="1px solid #dadce0" textAlign="right" w="120px">Tickets</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right" w="100px">% of Total</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {serviceData.map((svc, i) => (
            <Table.Row key={svc.id}>
              <Table.Cell>{i + 1}</Table.Cell>
              <Table.Cell>{svc.name}</Table.Cell>
              <Table.Cell borderLeft="1px solid #dadce0" textAlign="right">
                <Text color={svc.count > 0 ? "var(--blue)" : "var(--gray-8)"}>
                  {svc.count}
                </Text>
              </Table.Cell>
              <Table.Cell textAlign="right">
                <Text color="var(--gray-8)" fontSize="xs">
                  {totalTickets > 0 ? ((svc.count / totalTickets) * 100).toFixed(1) : 0}%
                </Text>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export default ArrivalRatesPerHour;
